import { type DesktopNotification, useNotificationStore } from '@khal-os/ui';
import { Bell, X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

function formatTime(timestamp: number): string {
	const diff = Date.now() - timestamp;
	if (diff < 60_000) return 'Just now';
	if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
	if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
	return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function NotificationCenter() {
	const centerOpen = useNotificationStore((s) => s.centerOpen);
	const history = useNotificationStore((s) => s.history);
	const clearHistory = useNotificationStore((s) => s.clearHistory);
	const closeCenter = useNotificationStore((s) => s.closeCenter);
	const dismissOne = useNotificationStore((s) => s.dismissNotification);
	const panelRef = useRef<HTMLDivElement>(null);

	const grouped = useMemo<Array<[string, DesktopNotification[]]>>(() => {
		const map = new Map<string, DesktopNotification[]>();
		for (const n of history) {
			const key = (n.appName?.trim() || 'System') as string;
			const list = map.get(key);
			if (list) list.push(n);
			else map.set(key, [n]);
		}
		return Array.from(map.entries());
	}, [history]);

	useEffect(() => {
		if (!centerOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
				closeCenter();
			}
		};
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeCenter();
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [centerOpen, closeCenter]);

	if (!centerOpen) return null;

	return (
		<div
			ref={panelRef}
			role="dialog"
			aria-label="Notification center"
			className="khal-anim-toast-in khal-notif-slide k-popover fixed z-[9400] flex w-80 flex-col"
			style={{
				bottom: 'var(--khal-notification-bottom)',
				right: 'var(--khal-notification-right, 16px)',
				maxHeight: 'min(520px, calc(100vh - 60px))',
				padding: 0,
			}}
		>
			<div
				className="k-menu-h"
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					padding: '10px 12px 8px',
					borderBottom: '1px solid var(--khal-border)',
				}}
			>
				<span style={{ flex: 1 }}>Notifications</span>
				{history.length > 0 && (
					<button type="button" className="k-btn k-btn-ghost k-btn-sm" onClick={clearHistory}>
						Clear all
					</button>
				)}
				<button
					type="button"
					className="k-btn k-btn-ghost k-btn-icon k-btn-sm"
					onClick={closeCenter}
					aria-label="Close notification center"
				>
					<X size={12} />
				</button>
			</div>

			<div className="flex-1 overflow-y-auto" style={{ padding: 4 }}>
				{history.length === 0 ? (
					<div className="k-empty" style={{ padding: '32px 16px' }}>
						<div className="k-empty-icon">
							<Bell size={20} />
						</div>
						<p>All caught up.</p>
					</div>
				) : (
					grouped.map(([appName, notifs]) => (
						<div key={appName}>
							<div className="k-menu-h" style={{ padding: '8px 10px 4px' }}>
								{appName}
							</div>
							{notifs.map((notif, i) => (
								<div
									key={`${notif.id}-${notif.timestamp}-${i}`}
									className="k-list-item"
									style={{
										gap: 10,
										padding: '8px 10px',
										borderBottom: 0,
										background:
											notif.urgency === 'critical'
												? 'color-mix(in oklch, var(--khal-error) 8%, transparent)'
												: 'transparent',
										borderRadius: 6,
									}}
								>
									{notif.icon ? (
										<img src={notif.icon} alt="" className="h-6 w-6 shrink-0 rounded" />
									) : (
										<div
											className="k-icon-cell k-icon-cell-sm"
											style={{ flexShrink: 0, color: 'var(--khal-tertiary)' }}
										>
											<Bell size={12} />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
											<span className="k-list-item-title" style={{ flex: 1, minWidth: 0 }}>
												{notif.summary}
											</span>
											<span
												className="k-list-item-desc"
												style={{ marginTop: 0, flexShrink: 0, fontFamily: 'var(--khal-font-mono)' }}
											>
												{formatTime(notif.timestamp)}
											</span>
										</div>
										{notif.body && (
											<p className="k-list-item-desc line-clamp-2" style={{ marginTop: 2 }}>
												{notif.body}
											</p>
										)}
									</div>
									<button
										type="button"
										className="k-btn k-btn-ghost k-btn-icon k-btn-sm"
										onClick={() => dismissOne(notif.id)}
										aria-label="Dismiss notification"
										style={{ flexShrink: 0 }}
									>
										<X size={11} />
									</button>
								</div>
							))}
						</div>
					))
				)}
			</div>
		</div>
	);
}
